import { useCallback, useEffect, useState } from 'react';
import { MessageSquareText, RefreshCw, Search, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const formatDate = (value) => value
  ? new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  : '-';

const selectClass = 'h-10 rounded-md border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]';

export default function AdminAgentChatPanel({ className = '' }) {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('waiting');
  const [reply, setReply] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get('/api/admin/agent-earn/team-chat');
      const next = response.data?.conversations || [];
      setConversations(next);
      setSelected((current) => {
        if (!current?.id) return current;
        return next.find((item) => item.id === current.id) || current;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load agent conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openConversation = async (conversation) => {
    setSelected(conversation);
    setStatus(conversation.status || 'waiting');
    setMessages([]);
    setLoadingMessages(true);
    try {
      const response = await api.get(`/api/admin/agent-earn/team-chat/${conversation.id}/messages`);
      const nextConversation = response.data?.conversation || conversation;
      setSelected(nextConversation);
      setStatus(nextConversation.status || 'waiting');
      setMessages(response.data?.messages || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load agent messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const updateStatus = async (nextStatus) => {
    setStatus(nextStatus);
    if (!selected?.id) return;
    try {
      await api.patch(`/api/admin/agent-earn/team-chat/${selected.id}/status`, { status: nextStatus });
      setSelected((current) => ({ ...current, status: nextStatus }));
      await refresh({ quiet: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update conversation status');
    }
  };

  const sendReply = async () => {
    const message = reply.trim();
    if (!selected?.id || !message || sending) return;
    setSending(true);
    try {
      await api.post(`/api/admin/agent-earn/team-chat/${selected.id}/messages`, {
        message,
        status,
      });
      setReply('');
      await Promise.all([openConversation(selected), refresh({ quiet: true })]);
      toast.success('Reply sent to agent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  const needle = search.trim().toLowerCase();
  const filtered = conversations.filter((conversation) => {
    if (!needle) return true;
    const agent = conversation.agent || {};
    return [
      conversation.subject,
      conversation.team_label,
      conversation.issue_type,
      conversation.last_message,
      agent.name,
      agent.email,
      agent.username,
    ].some((value) => String(value || '').toLowerCase().includes(needle));
  });
  const openCount = conversations.filter((item) => item.status !== 'resolved').length;

  return (
    <section
      className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-[#111827]/80 ${className}`}
      data-testid="admin-agent-chat-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-black text-white">
            <MessageSquareText className="h-5 w-5 text-[#D4AF37]" /> Agent Chat
          </h2>
          <p className="mt-1 text-xs text-gray-500">Tenant-scoped agent requests, response history, and resolution status.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-[#D4AF37]/15 px-3 py-1 text-xs font-black text-[#D4AF37]">{openCount} open</span>
          <Button type="button" size="icon" variant="outline" className="border-white/10 text-gray-200" onClick={() => refresh()} aria-label="Refresh agent conversations">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(280px,380px)_1fr]">
        <aside className="flex min-h-[220px] flex-col border-b border-white/10 md:min-h-0 md:border-b-0 md:border-r">
          <div className="relative border-b border-white/10 p-3">
            <Search className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} className="h-10 border-white/10 bg-[#0B0E14] pl-9 text-white" placeholder="Search agents or issues" />
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {loading ? (
              <div className="grid min-h-40 place-items-center"><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
              <div className="grid min-h-40 place-items-center px-4 text-center text-sm text-gray-500">No agent conversations found.</div>
            ) : filtered.map((conversation) => (
              <button
                type="button"
                key={conversation.id}
                onClick={() => openConversation(conversation)}
                className={`w-full rounded-lg border p-3 text-left transition ${selected?.id === conversation.id ? 'border-[#D4AF37]/60 bg-[#D4AF37]/10' : 'border-white/10 bg-black/20 hover:bg-white/[0.04]'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-bold text-white">{conversation.agent?.name || conversation.agent?.username || 'Agent'}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${conversation.status === 'resolved' ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`}>{conversation.status || 'open'}</span>
                </div>
                <p className="mt-0.5 truncate text-[11px] text-[#D4AF37]">{conversation.agent?.email || conversation.agent?.id || 'Private agent'}</p>
                <p className="mt-1 truncate text-sm font-semibold text-gray-200">{conversation.subject}</p>
                <p className="mt-1 truncate text-xs text-gray-500">{conversation.team_label} · {conversation.last_message}</p>
              </button>
            ))}
          </div>
        </aside>

        <div className="flex min-h-[420px] min-w-0 flex-col md:min-h-0">
          {!selected ? (
            <div className="grid flex-1 place-items-center p-8 text-center">
              <div>
                <MessageSquareText className="mx-auto mb-3 h-10 w-10 text-[#D4AF37]" />
                <p className="font-bold text-white">Select an agent conversation</p>
                <p className="mt-1 text-sm text-gray-500">Messages and resolution controls appear here.</p>
              </div>
            </div>
          ) : (
            <>
              <header className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-4">
                <div className="min-w-0">
                  <p className="truncate text-lg font-black text-white">{selected.subject}</p>
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {selected.agent?.name || 'Agent'} · {selected.agent?.email || selected.agent?.id || 'private'} · {selected.team_label}
                  </p>
                </div>
                <select value={status} onChange={(event) => updateStatus(event.target.value)} className={selectClass} aria-label="Conversation status">
                  <option value="open">Open</option>
                  <option value="waiting">Waiting</option>
                  <option value="resolved">Resolved</option>
                </select>
              </header>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                {loadingMessages ? (
                  <div className="grid h-full place-items-center"><div className="spinner" /></div>
                ) : messages.length === 0 ? (
                  <div className="grid h-full place-items-center text-sm text-gray-500">No messages in this conversation.</div>
                ) : messages.map((message) => {
                  const fromAgent = message.sender_role === 'agent';
                  return (
                    <div key={message.id} className={`flex ${fromAgent ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[84%] rounded-2xl px-4 py-3 ${fromAgent ? 'bg-white/10 text-gray-100' : 'bg-[#0f8fa6] text-white'}`}>
                        <p className="mb-1 text-[10px] font-black uppercase tracking-wide opacity-70">{fromAgent ? (selected.agent?.name || 'Agent') : (message.sender_name || 'Team')}</p>
                        <p className="whitespace-pre-wrap break-words text-sm leading-6">{message.body}</p>
                        <p className="mt-1 text-right text-[10px] opacity-60">{formatDate(message.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <footer className="space-y-2 border-t border-white/10 p-3">
                <Textarea
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={2}
                  className="resize-none border-white/10 bg-[#0B0E14] text-white"
                  placeholder="Reply from the selected team..."
                />
                <Button type="button" onClick={sendReply} disabled={!reply.trim() || sending} className="w-full bg-[#D4AF37] font-black text-black hover:bg-[#F5D76E]">
                  <Send className="mr-2 h-4 w-4" /> {sending ? 'Sending...' : 'Send Reply'}
                </Button>
              </footer>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
